// What a person's card in the Tree view (and the chart export) shows besides their name.
// One choice for every card, remembered in this browser. Cards grow taller to fit the
// details that are turned on, and every card in a chart is the same height.

import { ageOf, ageText } from '../../model/countdown';
import { headlineRelations, relate } from '../../model/kinship';
import { citationsAbout } from '../../model/research';
import { lsGet, lsSet } from '../../model/db';
import { genderLabel, isLiving, LIFE_STATUS_LABELS, lifeSpan, lifeStatus, primaryName, shortName } from '../../model/people';
import type { Id, Person, Tree } from '../../model/types';
import { font, textWidth } from '../views/tableLayout';
import { CARD_H, CARD_W } from './layout';

export type CardField =
  | 'photo' | 'dates' | 'status' | 'pronouns'
  | 'age' | 'birthplace' | 'occupation'
  | 'nickname' | 'gender' | 'relation'
  | 'tags' | 'research';

export const CARD_FIELDS: { key: CardField; label: string; group: string }[] = [
  { key: 'photo', label: 'Photo', group: 'Basics' },
  { key: 'dates', label: 'Life dates', group: 'Basics' },
  { key: 'status', label: 'Living status', group: 'Basics' },
  { key: 'pronouns', label: 'Pronouns', group: 'Basics' },
  { key: 'age', label: 'Age', group: 'Life' },
  { key: 'birthplace', label: 'Birthplace', group: 'Life' },
  { key: 'occupation', label: 'Occupation', group: 'Life' },
  { key: 'nickname', label: 'Nickname', group: 'Identity' },
  { key: 'gender', label: 'Gender', group: 'Identity' },
  { key: 'relation', label: 'Relationship to home person', group: 'Identity' },
  { key: 'tags', label: 'Tags', group: 'Research' },
  { key: 'research', label: 'Sources, media, and to-dos', group: 'Research' },
];

export const DEFAULT_CARD_FIELDS: CardField[] = ['photo', 'dates', 'pronouns'];

const KEY = 'relationships.cardFields';

export function loadCardFields(): CardField[] {
  try {
    const saved = JSON.parse(lsGet(KEY) ?? 'null');
    if (Array.isArray(saved)) return CARD_FIELDS.map((f) => f.key).filter((k) => saved.includes(k));
  } catch {
    /* fall through */
  }
  return DEFAULT_CARD_FIELDS;
}

export function saveCardFields(fields: CardField[]) {
  lsSet(KEY, JSON.stringify(fields));
}

/** The text lines (below the name) that `fields` call for. */
export function textFields(fields: CardField[]): CardField[] {
  return CARD_FIELDS.map((f) => f.key).filter((k) => k !== 'photo' && fields.includes(k));
}

const FIRST_LINE_Y = 42;
export const LINE_H = 15;
/** Between the baselines of a name's two lines. */
const NAME_LH = 16;
/** Room above the name's baseline and below the last line's, for the letters themselves. */
const ASCENT = 13;
const DESCENT = 4;
/** The least space kept above and below the text. */
const MARGIN = 6;

const nameFont = () => font(13.5, 650);
const detailFont = (first: boolean) => font(first ? 12 : 11, 400);
/** Where text starts, beside the photo (or without one), and the width it gets before the card's edge. */
export const textLeft = (photo: boolean) => (photo ? 48 : 14);
const textRoom = (photo: boolean) => CARD_W - textLeft(photo) - 6;

/** Height of the name (1 or 2 lines) and `n` detail lines, from the top of the letters to the bottom. */
function blockHeight(nameLines: number, n: number): number {
  return ASCENT + (nameLines - 1) * NAME_LH + (n ? FIRST_LINE_Y - 24 + (n - 1) * LINE_H : 0) + DESCENT;
}

/**
 * Card height for these fields: room for the name and each detail line, never shorter than
 * today's card. `twoLineNames` (some name in the tree needs two lines) makes room for that too,
 * since every card in a chart is the same height.
 */
export function cardHeight(fields: CardField[], twoLineNames = false): number {
  const n = textFields(fields).length;
  const h = Math.max(CARD_H, FIRST_LINE_Y + (n - 1) * LINE_H + 9);
  return twoLineNames ? Math.max(h, blockHeight(2, n) + MARGIN * 2) : h;
}

/** Whether a name needs a second line on a card. */
export function nameWraps(name: string, photo: boolean): boolean {
  return textWidth(name, nameFont()) > textRoom(photo);
}

/** `text` cut to fit `width` in font `f`, ending in an ellipsis when cut. Measured, not counted. */
export function fitWidth(text: string, width: number, f: string): string {
  if (textWidth(text, f) <= width) return text;
  // Cut between characters, not inside one (an emoji is two UTF-16 units).
  const chars = [...text];
  const cut = (n: number) => chars.slice(0, n).join('').trimEnd() + '…';
  let lo = 0;
  let hi = chars.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (textWidth(cut(mid), f) <= width) lo = mid;
    else hi = mid - 1;
  }
  return cut(lo);
}

/** A name on one line, or on two (broken after a space or hyphen) when it doesn't fit and `two` allows. */
function nameLines(name: string, width: number, two: boolean): string[] {
  const f = nameFont();
  if (textWidth(name, f) <= width) return [name];
  if (!two) return [fitWidth(name, width, f)];
  // The latest break that leaves a first line that fits; a single long word breaks anywhere.
  let cut = 0;
  for (const m of name.matchAll(/[\s-]/g)) if (textWidth(name.slice(0, m.index! + 1).trimEnd(), f) <= width) cut = m.index! + 1;
  if (!cut) cut = Math.max(1, fitWidth(name, width, f).length - 1);
  return [name.slice(0, cut).trimEnd(), fitWidth(name.slice(cut).trim(), width, f)];
}

export interface CardText {
  name: string[];
  nameY: number[];
  /** The detail lines, cut to fit, and their baselines. */
  details: string[];
  detailY: number[];
}

/**
 * Where a card's text goes: the name (on two lines if it needs them and there's room) and the
 * detail lines under it, centered top to bottom, so a card with only a name lines up with its photo.
 */
export function cardText(name: string, detailLines: string[], photo: boolean, h: number): CardText {
  const n = detailLines.length;
  const width = textRoom(photo);
  const lines = nameLines(name, width, blockHeight(2, n) + MARGIN * 2 <= h);
  const top = Math.max(MARGIN, (h - blockHeight(lines.length, n)) / 2);
  const first = Math.round(top + ASCENT);
  const nameY = lines.map((_, i) => first + i * NAME_LH);
  const detailStart = nameY[nameY.length - 1] + (FIRST_LINE_Y - 24);
  return {
    name: lines,
    nameY,
    details: detailLines.map((l, i) => fitWidth(l, width, detailFont(i === 0))),
    detailY: detailLines.map((_, i) => detailStart + i * LINE_H),
  };
}

// Per-tree lookups, computed once per Tree object (every edit makes a new one).
const researchCache = new WeakMap<Tree, Map<Id, string>>();
const relationCache = new WeakMap<Tree, Map<Id, string>>();

function plural(n: number, one: string, many = one + 's') {
  return `${n} ${n === 1 ? one : many}`;
}

function researchLine(tree: Tree, id: Id): string {
  let cache = researchCache.get(tree);
  if (!cache) {
    cache = new Map();
    const media = new Map<Id, number>();
    for (const m of Object.values(tree.media)) for (const x of m.people) media.set(x.personId, (media.get(x.personId) ?? 0) + 1);
    const todos = new Map<Id, number>();
    for (const t of Object.values(tree.todos)) if (t.status !== 'done') for (const p of t.people) todos.set(p, (todos.get(p) ?? 0) + 1);
    for (const pid of Object.keys(tree.people)) {
      const s = citationsAbout(tree, pid).length;
      const parts = [s && plural(s, 'source'), media.get(pid) && plural(media.get(pid)!, 'media', 'media'), todos.get(pid) && plural(todos.get(pid)!, 'to-do')].filter(Boolean);
      if (parts.length) cache.set(pid, parts.join(' · '));
    }
    researchCache.set(tree, cache);
  }
  return cache.get(id) ?? '';
}

function relationLine(tree: Tree, id: Id): string {
  const home = tree.homePersonId;
  if (!home || !tree.people[home]) return '';
  if (home === id) return 'Home person';
  let cache = relationCache.get(tree);
  if (!cache) relationCache.set(tree, (cache = new Map()));
  if (!cache.has(id)) {
    // Only cards that are drawn get here (the chart culls), so this stays cheap on big trees.
    const term = headlineRelations(relate(tree, home, id))[0]?.term;
    cache.set(id, term ? `${shortName(tree.people[home])}’s ${term}` : '');
  }
  return cache.get(id)!;
}

/** The detail lines for one card, in field order; empty fields are left out. */
export function cardLines(tree: Tree, p: Person, fields: CardField[], now = new Date()): string[] {
  const out: string[] = [];
  for (const f of textFields(fields)) {
    let text = '';
    switch (f) {
      case 'dates':
        // With no dates, the old fallback ("Living") still applies unless the status has its own line.
        text = lifeSpan(p) || (!fields.includes('status') && isLiving(p) ? 'Living' : '');
        break;
      case 'status': {
        const s = lifeStatus(p);
        text = s === 'unknown' ? 'Status not recorded' : LIFE_STATUS_LABELS[s];
        break;
      }
      case 'pronouns':
        text = p.pronouns ?? '';
        break;
      case 'age': {
        const a = ageOf(p, now);
        text = a ? (a.atDeath ? `Died at ${ageText(a)}` : `Age ${ageText(a)}`) : '';
        break;
      }
      case 'birthplace': {
        const place = p.events.find((e) => e.type === 'birth')?.place;
        text = place ? `Born in ${place}` : '';
        break;
      }
      case 'occupation':
        text = [...p.events].reverse().find((e) => e.type === 'occupation' && e.label)?.label ?? '';
        break;
      case 'nickname': {
        // The nickname of the name shown, never of a hidden name or deadname.
        const nick = primaryName(p)?.nickname;
        text = nick ? `“${nick}”` : '';
        break;
      }
      case 'gender':
        text = p.gender.identity === 'unknown' ? '' : genderLabel(p.gender);
        break;
      case 'relation':
        text = relationLine(tree, p.id);
        break;
      case 'tags':
        text = p.tags.join(', ');
        break;
      case 'research':
        text = researchLine(tree, p.id);
        break;
    }
    if (text) out.push(text);
  }
  return out;
}
